import type { UseQueryOptions, UseQueryResult } from 'react-query';
import { useQuery, useQueryClient } from 'react-query';

import type { IQueryKeyFactory } from '~/helpers/types';
import type { ApiError } from '~/lib/api';
import type {
  IApiGetVideosRequestBody,
  IApiGetVideosResponseBody,
} from '../VideosApi.types';
import { useApi } from '~/lib/api/useApi';
import { videoQueryKeyFactory } from './useVideoQuery';

export type IUseVideosQueryValues = {
  appId: string;
  body: IApiGetVideosRequestBody;
};

export const videosQueryKeyFactory: IQueryKeyFactory<IUseVideosQueryValues> = (
  values,
) =>
  values &&
  (values.body
    ? (['videos', values.appId, values.body] as const)
    : (['videos', values.appId] as const));

export const useVideosQuery = (
  values?: IUseVideosQueryValues,
  options?: UseQueryOptions<IApiGetVideosResponseBody, ApiError>,
): UseQueryResult<IApiGetVideosResponseBody, ApiError> => {
  const api = useApi();
  const queryClient = useQueryClient();

  return useQuery({
    queryKey: videosQueryKeyFactory(values),
    queryFn: values
      ? () => api.videos.getAll(values.appId, values.body)
      : undefined,
    onSuccess: (videos) => {
      videos.list?.forEach((video) => {
        const queryKey = videoQueryKeyFactory({
          appId: video.appId,
          videoId: video._id,
        });
        if (queryKey) {
          queryClient.setQueryData(queryKey, video);
        }
      });
      options?.onSuccess?.(videos);
    },
    ...options,
  });
};
